import { useEffect, useState, useCallback } from "react";
import dayjs from "dayjs";
import ProjectCard from "../cards/ProjectCard";
import StatusTabs from "../elements/StatusTabs";
import { getMyProjects } from "../../api/projects";
import Pagination from "../elements/Pagination";

interface Project {
  id: number;
  title: string;
  summary: string;
  status: string;
  createdAt: string;
  publishedAt: string;
  closedAt: string;
  address: string;
}

const Projects: React.FC = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [statusesCount, setStatusesCount] = useState<{ [key: string]: number }>(
    {}
  );
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const rowsPerPage = 6;

  const storedUserType = localStorage.getItem("userType");

  const assignorStatuses = [
    "All",
    "Draft",
    "Open",
    "Ongoing",
    "Cancelled",
    "Completed",
  ];
  const othersStatuses = ["All", "Open", "Ongoing", "Cancelled", "Completed"];
  const currentStatuses =
    storedUserType === "ASSIGNOR" ? assignorStatuses : othersStatuses;

  const fetchProjects = useCallback(async () => {
    try {
      const data = await getMyProjects(
        currentPage,
        rowsPerPage,
        selectedStatus !== "all" ? selectedStatus : undefined
      );

      const normalized = data.projects.map((p: Project) => ({
        ...p,
        status: p.status.toLowerCase(),
      }));

      setProjects(normalized);
      setTotalPages(data.totalPages || 1);

      setStatusesCount({
        all: data.statusesCount.all || 0,
        draft: data.statusesCount.draft || 0,
        open: data.statusesCount.open || 0,
        ongoing: data.statusesCount.ongoing || 0,
        cancelled: data.statusesCount.cancelled || 0,
        completed: data.statusesCount.completed || 0,
      });
    } catch {}
  }, [currentPage, selectedStatus]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const handleStatusClick = (status: string) => {
    setSelectedStatus(status.toLowerCase());
    setCurrentPage(1);
  };

  return (
    <div className="flex flex-row">
      <div className="flex-1 p-6 ml-[17.5rem]">
        <h1 className="text-2xl font-bold mb-4">My projects</h1>

        <div className="mb-4 border-b-2 pb-4">
          <StatusTabs
            statuses={currentStatuses}
            selected={selectedStatus}
            onSelect={handleStatusClick}
            counts={statusesCount}
          />
        </div>

        {projects.length > 0 ? (
          <div className="max-w-screen grid xl:grid-cols-3 lg:grid-cols-2 grid-cols-1 gap-4">
            {projects.map((project) => {
              const displayDate =
                project.status === "draft"
                  ? project.createdAt
                  : project.publishedAt || project.closedAt;

              return (
                <ProjectCard
                  key={project.id}
                  id={project.id}
                  title={project.title}
                  publishedAt={dayjs(displayDate).format("DD.MM.YYYY")}
                  status={project.status}
                  summary={project.summary || "No summary available."}
                />
              );
            })}
          </div>
        ) : (
          <div className="text-center text-gray-500 text-sm py-12">
            No projects found.
          </div>
        )}

        {totalPages > 1 && (
          <div className="border-t mt-4 pt-4">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default Projects;
